import React, { useEffect, useState, useCallback, useRef } from 'react';
import useRacePlayback from '../useRacePlayback';
import PlaybackMode from '../components/PlaybackMode';
import AdminKeyGate from '../components/AdminKeyGate';
import VideoOverlay from '../components/VideoOverlay';
import RacePodium from '../components/RacePodium';
import { setAdminKey, getAdminRaces, getLiveLeaderboard, getRaceLeaderboard } from '../api';


const POLL_INTERVAL = 15_000;

const kf = `
  @keyframes pwpulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.3; transform: scale(0.6); }
  }
  @keyframes redflag {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.80; }
  }
  @keyframes rowIn {
    from { opacity: 0; transform: translateX(-6px); }
    to   { opacity: 1; transform: translateX(0); }
  }
`;

const COMPOUNDS = {
  SOFT:         { bg: '#e8002d', fg: '#fff', label: 'S' },
  MEDIUM:       { bg: '#f9b000', fg: '#111', label: 'M' },
  HARD:         { bg: '#e0e0e0', fg: '#111', label: 'H' },
  INTERMEDIATE: { bg: '#22c55e', fg: '#fff', label: 'I' },
  WET:          { bg: '#3b82f6', fg: '#fff', label: 'W' },
};

const PODIUM = ['#FFD700', '#C0C0C0', '#CD7F32'];
const F1_PTS = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];

function TyreBadge({ compound, laps }) {
  const c = COMPOUNDS[(compound || '').toUpperCase()] ?? { bg: '#2a2a2a', fg: '#555', label: '?' };

  if (!laps) {
    return (
      <span style={{
        width: 28, height: 28, borderRadius: '50%',
        background: c.bg, color: c.fg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: 13,
        flexShrink: 0,
        boxShadow: `0 0 0 1px ${c.bg}55`,
      }}>
        {c.label}
      </span>
    );
  }

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'stretch',
      borderRadius: 6, overflow: 'hidden',
      flexShrink: 0, height: 28,
      outline: `1px solid ${c.bg}44`,
      outlineOffset: 0,
    }}>
      {/* Compound letter */}
      <span style={{
        width: 26,
        background: c.bg, color: c.fg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: 13,
        letterSpacing: '0.01em',
      }}>
        {c.label}
      </span>
      {/* Divider */}
      <span style={{ width: 1, background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />
      {/* Lap count */}
      <span style={{
        padding: '0 8px',
        background: '#131313',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: 2,
        fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
        color: '#bbb',
        letterSpacing: '0.02em',
      }}>
        {laps}
        <span style={{ fontSize: 9, color: '#444', fontWeight: 400, lineHeight: 1, marginTop: 1 }}>L</span>
      </span>
    </span>
  );
}

function bannerConfig(trackStatus) {
  if (!trackStatus || trackStatus === 'clear') return null;
  if (trackStatus === 'red')                   return { bg: '#c00020', fg: '#fff', pulse: true };
  if (trackStatus === 'sc' || trackStatus === 'sc_ending') return { bg: '#92400e', fg: '#fef3c7', pulse: false };
  if (trackStatus === 'vsc' || trackStatus === 'vsc_ending') return { bg: '#1e3a5f', fg: '#bfdbfe', pulse: false };
  if (trackStatus === 'chequered')             return { bg: '#1a1a1a', fg: '#d4d4d4', pulse: false };
  return null;
}

function elapsedTime(raceDate) {
  const ms = Date.now() - new Date(raceDate);
  if (ms < 0) return 'LIVE';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1_000);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
}

// ── outer shell: admin gate only ──────────────────────────────────────────────
export default function PitWallPage() {
  const [adminKey, setAdminKeyState] = useState(() => {
    const stored = sessionStorage.getItem('f1tips_adminkey');
    if (stored) setAdminKey(stored);
    return stored;
  });

  const clearKey = () => {
    sessionStorage.removeItem('f1tips_adminkey');
    setAdminKey(null);
    setAdminKeyState(null);
  };

  if (!adminKey) {
    return (
      <div style={{
        minHeight: '100vh', background: '#080808',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <AdminKeyGate onKey={k => { setAdminKey(k); setAdminKeyState(k); }} />
      </div>
    );
  }

  return <Dashboard onAuthError={clearKey} />;
}

// ── dashboard (all hooks live here) ──────────────────────────────────────────
function Dashboard({ onAuthError }) {
  const [liveRace, setLiveRace]     = useState(null);
  const [raceLoading, setRaceLoading] = useState(true);
  const [authError, setAuthError]   = useState(false);
  const [liveData, setData]             = useState(null);
  const [elapsed, setElapsed]       = useState('--:--');
  const [countdown, setCountdown]   = useState(POLL_INTERVAL / 1000);
  const [showVideo, setShowVideo]   = useState(false);
  const [showPodium, setShowPodium] = useState(false);
  const [podiumLeaderboard, setPodiumLeaderboard] = useState([]);
  const playback = useRacePlayback(liveRace?.id);
  // Until server metadata resolves, NEVER project current live data.
  const pubMode = playback.mode !== 'live';
  const data = pubMode ? (playback.frame?.ok && playback.frame.dashboard ? {
    ...playback.frame.dashboard, liveTop10: playback.frame.dashboard.top10 || [],
    leaderboard: [], totalLaps: playback.frame.race.totalLaps,
    updatedAt: new Date(playback.frame.targetMs).toISOString(),
  } : null) : liveData;
  const revealedRaceIdRef = useRef(null);

  // Find the active race: prefer locked race in live window, fall back to any
  // locked race without results, then to any race with a video_url set (pre-race).
  useEffect(() => {
    getAdminRaces().then(races => {
      const inWindow = races.find(r => {
        if (!r.locked || r.official_top10) return false;
        if (!r.race_date) return false;
        const h = (Date.now() - new Date(r.race_date)) / 36e5;
        return h >= 0 && h < 12;
      });
      const lockedFallback = races.find(r => r.locked && !r.official_top10);
      const videoFallback = races.find(r => !r.cancelled && !r.official_top10 && r.video_url);
      setLiveRace(inWindow ?? lockedFallback ?? videoFallback ?? null);
    }).catch(e => {
      if (e.response?.status === 401) setAuthError(true);
    }).finally(() => setRaceLoading(false));
  }, []);

  const openPodium = useCallback((raceId) => {
    getRaceLeaderboard(raceId).then(lb => {
      setPodiumLeaderboard(lb.filter(r => !r.is_staff));
      setShowPodium(true);
    }).catch(() => {});
  }, []);

  // Poll the pinned race for results being posted (initial load only fetches the race list once,
  // so this is what lets Pit Wall notice results posted from another tab and auto-reveal the podium)
  useEffect(() => {
    if (!liveRace) return;
    const raceId = liveRace.id;
    const check = () => {
      getAdminRaces().then(races => {
        const fresh = races.find(r => r.id === raceId);
        if (!fresh) return;
        setLiveRace(prev => (prev && prev.id === raceId ? { ...prev, ...fresh } : prev));
        if (!pubMode && fresh.official_top10 && revealedRaceIdRef.current !== raceId) {
          revealedRaceIdRef.current = raceId;
          openPodium(raceId);
        }
      }).catch(e => {
        if (e.response?.status === 401) setAuthError(true);
      });
    };
    check();
    const id = setInterval(check, POLL_INTERVAL);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveRace?.id, openPodium, pubMode]);

  // Elapsed timer (ticks every second)
  useEffect(() => {
    if (!liveRace?.race_date) return;
    const tick = () => setElapsed(elapsedTime(liveRace.race_date));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [liveRace]);

  // Countdown to next refresh
  useEffect(() => {
    const id = setInterval(() => setCountdown(c => (c <= 1 ? POLL_INTERVAL / 1000 : c - 1)), 1000);
    return () => clearInterval(id);
  }, []);

  // Live data fetch
  const fetchData = useCallback(() => {
    if (!liveRace || pubMode) return;
    getLiveLeaderboard(liveRace.id)
      .then(d => { setData(d); setCountdown(POLL_INTERVAL / 1000); })
      .catch(() => {});
  }, [liveRace, pubMode]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchData]);

  // ── loading / no race ────────────────────────────────────────────────────
  if (raceLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#080808', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#333', letterSpacing: '0.12em' }}>INITIALISING PIT WALL...</p>
      </div>
    );
  }

  if (authError) {
    return (
      <div style={{ minHeight: '100vh', background: '#080808', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 40, fontWeight: 800, color: '#e8002d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Admin Key Rejected</p>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#444' }}>Your stored admin key is invalid or expired</p>
        <button
          onClick={onAuthError}
          style={{
            background: '#f9b000', border: 'none', borderRadius: 4,
            color: '#111', fontFamily: 'var(--font-mono)', fontSize: 12,
            fontWeight: 700, letterSpacing: '0.1em', padding: '10px 18px',
            cursor: 'pointer', marginTop: 4,
          }}
        >
          RE-ENTER KEY
        </button>
      </div>
    );
  }

  if (!liveRace) {
    return (
      <div style={{ minHeight: '100vh', background: '#080808', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 40, fontWeight: 800, color: '#f9b000', textTransform: 'uppercase', letterSpacing: '0.1em' }}>No Live Race</p>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#444' }}>Pit Wall activates when a race is locked by admin</p>
      </div>
    );
  }

  const latestControl = data?.raceControl?.slice(-1)[0] ?? null;
  const lastScMsg     = data?.raceControl?.slice().reverse().find(m => m.category === 'SafetyCar' || m.category === 'Vsc');
  const scStale       = lastScMsg && ((pubMode && playback.frame ? playback.frame.targetMs : Date.now()) - new Date(lastScMsg.date).getTime()) > 3 * 60 * 1000;
  const effectiveStatus = scStale ? 'clear' : data?.trackStatus;
  const banner        = bannerConfig(effectiveStatus);
  const bannerH       = banner ? 68 : 0;
  const poolLeaderboard = data?.leaderboard ?? [];

  return (
    <React.Fragment>
      <div style={{ position: 'fixed', right: 12, bottom: 12, zIndex: 5000, background: '#111', padding: '2px 10px', borderRadius: 5 }}>
        <PlaybackMode mode={playback.mode} onMode={playback.chooseMode} settings={playback.settings} status={playback.status} />
        {pubMode && playback.status !== 'ready' && <div role="status" style={{ color: '#f9b000' }}>TV sync: {playback.status} — no live fallback</div>}
        {pubMode && <div style={{ color: '#999', fontSize: 10 }}>Historical prediction standings unavailable until Assignment 4.</div>}
      </div>
    <div style={{
      position: 'fixed', inset: 0,
      background: '#080808',
      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(255,255,255,0.016) 28px)',
      display: 'grid',
      gridTemplateRows: `${bannerH}px 68px 1fr 52px`,
      overflow: 'hidden',
      transition: 'grid-template-rows 0.35s ease',
    }}>
      <style>{kf}</style>
      {!pubMode && showVideo && liveRace?.video_url && (
        <VideoOverlay url={liveRace.video_url} onClose={() => setShowVideo(false)} />
      )}
      {!pubMode && showPodium && liveRace?.official_top10 && (
        <RacePodium
          race={liveRace}
          leaderboard={podiumLeaderboard}
          onClose={() => setShowPodium(false)}
        />
      )}

      {/* Red left-edge stripe */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: '#e8002d', zIndex: 10 }} />

      {/* ── RACE CONTROL BANNER ───────────────────────────────────── */}
      <div style={{
        overflow: 'hidden',
        background: banner?.bg ?? 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: banner?.pulse ? 'redflag 1.4s ease-in-out infinite' : 'none',
      }}>
        {banner && latestControl && (
          <span style={{
            fontFamily: 'var(--font-display)',
            fontSize: 30, fontWeight: 800,
            letterSpacing: '0.16em', textTransform: 'uppercase',
            color: banner.fg,
          }}>
            {latestControl.message}
          </span>
        )}
      </div>

      {/* ── HEADER ───────────────────────────────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        padding: '0 28px 0 20px',
        borderBottom: '1px solid #181818',
        background: '#0c0c0c',
      }}>
        {/* Race name */}
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#e8002d', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 2 }}>
            {liveRace.season} · Round {liveRace.round}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: '#f0f0f0', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1 }}>
              {liveRace.name}
            </div>
            {!pubMode && liveRace.video_url && (
              <button
                onClick={() => setShowVideo(true)}
                style={{
                  background: '#e8002d', border: 'none', borderRadius: 4,
                  color: '#fff', fontFamily: 'var(--font-mono)', fontSize: 10,
                  fontWeight: 700, letterSpacing: '0.1em', padding: '4px 10px',
                  cursor: 'pointer', flexShrink: 0,
                }}
              >
                ▶ VIDEO
              </button>
            )}
            {!pubMode && liveRace.official_top10 && (
              <button
                onClick={() => openPodium(liveRace.id)}
                style={{
                  background: '#f9b000', border: 'none', borderRadius: 4,
                  color: '#111', fontFamily: 'var(--font-mono)', fontSize: 10,
                  fontWeight: 700, letterSpacing: '0.1em', padding: '4px 10px',
                  cursor: 'pointer', flexShrink: 0,
                }}
              >
                🏆 PODIUM
              </button>
            )}
          </div>
        </div>

        {/* Lap counter + elapsed time */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#444', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 0 }}>
            {pubMode ? 'Lap' : data?.currentLap ? 'Lap' : 'Elapsed'}
          </div>
          {data?.currentLap ? (
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 44, fontWeight: 800, color: '#f9b000', lineHeight: 1, letterSpacing: '-0.02em' }}>
              {data.currentLap}
              {data?.totalLaps && <span style={{ color: '#555' }}> / {data.totalLaps}</span>}
            </div>
          ) : (
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 44, fontWeight: 800, color: '#f9b000', lineHeight: 1, letterSpacing: '-0.02em' }}>
              {pubMode ? '—' : elapsed}
            </div>
          )}
          {data?.currentLap && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#444', marginTop: 2 }}>
              {pubMode ? '—' : elapsed}
            </div>
          )}
        </div>

        {/* Weather */}
        <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
          {data?.weather ? (
            <>
              <span style={{ color: '#555' }}>TRACK </span>
              <span style={{ color: '#e0e0e0', fontWeight: 700 }}>{Math.round(data.weather.trackTemp)}°C</span>
              <span style={{ color: '#333', margin: '0 10px' }}>·</span>
              <span style={{ color: '#555' }}>AIR </span>
              <span style={{ color: '#e0e0e0', fontWeight: 700 }}>{Math.round(data.weather.airTemp)}°C</span>
              <span style={{ color: '#333', margin: '0 10px' }}>·</span>
              <span style={{ color: data.weather.rainfall > 0 ? '#3b82f6' : '#444', fontWeight: 700 }}>
                {data.weather.rainfall > 0 ? 'WET' : 'DRY'}
              </span>
            </>
          ) : (
            <span style={{ color: '#2a2a2a' }}>— weather —</span>
          )}
        </div>
      </div>

      {/* ── MAIN GRID ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '58fr 42fr', overflow: 'hidden' }}>

        {/* LEFT: TIMING TOWER */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid #161616', paddingLeft: 4 }}>
          {/* Section label */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 20px', height: 34, flexShrink: 0,
            borderBottom: '1px solid #161616',
          }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700, color: '#e8002d', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              Live Timing
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 10, color: '#444' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f9b000', display: 'inline-block', animation: 'pwpulse 1.4s ease-in-out infinite' }} />
              LIVE
            </span>
          </div>

          {/* 10 driver rows */}
          <div style={{ flex: 1, display: 'grid', gridTemplateRows: 'repeat(10, 1fr)', overflow: 'hidden' }}>
            {data?.liveTop10 ? data.liveTop10.map((acr, i) => {
              const headshot   = data.headshots?.[acr];
              const teamColour = data.teamColours?.[acr];
              const rawGap     = data.gaps?.[acr];
              const gap        = rawGap != null ? (typeof rawGap === 'number' ? '+' + rawGap.toFixed(3) : String(rawGap).replace(/(\.\d{3})\d+/, '$1')) : null;
              const fullName   = data.fullNames?.[acr];
              const tyre       = data.tyres?.[acr];
              const tyreLaps   = data.tyreLaps?.[acr];
              const posColor   = PODIUM[i] ?? '#f9b000';
              const tc         = teamColour ? `#${teamColour}` : '#333';

              return (
                <div key={acr} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  paddingRight: 20,
                  borderBottom: '1px solid #0f0f0f',
                  background: i < 3 ? 'rgba(255,255,255,0.01)' : 'transparent',
                  animation: 'rowIn 0.3s ease both',
                  animationDelay: `${i * 35}ms`,
                  overflow: 'hidden',
                }}>
                  {/* Team stripe */}
                  <div style={{ width: 5, alignSelf: 'stretch', background: tc, flexShrink: 0 }} />

                  {/* Position */}
                  <span style={{
                    fontFamily: 'var(--font-display)', fontSize: 48, fontWeight: 800,
                    color: posColor, lineHeight: 1,
                    width: 68, textAlign: 'center', flexShrink: 0,
                    letterSpacing: '-0.02em',
                  }}>
                    {i + 1}
                  </span>

                  {/* Headshot */}
                  {headshot
                    ? <img src={headshot} alt={acr} style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${tc}`, flexShrink: 0 }} />
                    : <div style={{ width: 42, height: 42, borderRadius: '50%', background: `${tc}22`, border: `2px solid ${tc}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: tc }}>{acr}</div>
                  }

                  {/* Acronym + name */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: '#f0f0f0', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1 }}>
                      {acr}
                    </div>
                    {fullName && (
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: '#444', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {fullName}
                      </div>
                    )}
                  </div>

                  {/* F1 points for this position */}
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#333', flexShrink: 0 }}>
                    {F1_PTS[i]}pt
                  </span>

                  {/* Tyre */}
                  {tyre && <TyreBadge compound={tyre} laps={tyreLaps} />}

                  {/* Gap */}
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 700,
                    color: i === 0 ? '#f9b000' : '#666',
                    minWidth: 88, textAlign: 'right', flexShrink: 0,
                  }}>
                    {i === 0 ? 'LEADER' : (gap ?? '—')}
                  </span>
                </div>
              );
            }) : (
              <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2a2a2a', fontFamily: 'var(--font-mono)', fontSize: 13, letterSpacing: '0.1em' }}>
                AWAITING LIVE DATA
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: PREDICTION POOL */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Section label */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 20px', height: 34, flexShrink: 0,
            borderBottom: '1px solid #161616',
          }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700, color: '#e8002d', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              Prediction Pool
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#333', letterSpacing: '0.06em' }}>
              {poolLeaderboard.length} ENTRIES
            </span>
          </div>

          {/* Participant rows */}
          <div style={{
            flex: 1,
            display: 'grid',
            gridTemplateRows: poolLeaderboard.length > 0 ? `repeat(${poolLeaderboard.length}, 1fr)` : '1fr',
            overflow: 'hidden',
          }}>
            {poolLeaderboard.length > 0 ? poolLeaderboard.map((row, i) => {
              const isLeader = i === 0;
              const maxScore = poolLeaderboard[0]?.score || 1;
              const barPct   = maxScore > 0 ? Math.round((row.score / maxScore) * 100) : 0;
              const hits     = Object.values(row.breakdown || {}).filter(p => p > 0).length;

              return (
                <div key={row.user_name} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '0 20px',
                  background: isLeader ? 'rgba(249,176,0,0.055)' : 'transparent',
                  borderLeft: `3px solid ${isLeader ? 'rgba(249,176,0,0.45)' : 'transparent'}`,
                  borderBottom: '1px solid #0f0f0f',
                  transition: 'background 0.5s ease',
                }}>
                  {/* Rank */}
                  <span style={{
                    fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 800,
                    color: isLeader ? '#f9b000' : '#2a2a2a',
                    width: 36, flexShrink: 0, textAlign: 'center', lineHeight: 1.2,
                  }}>
                    {i + 1}
                  </span>

                  {/* Name + bar */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700,
                      color: isLeader ? '#f9b000' : '#d0d0d0',
                      textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.2,
                      marginBottom: 4,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {row.user_name}
                    </div>
                    <div style={{ height: 3, background: '#141414', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${barPct}%`,
                        background: isLeader ? '#f9b000' : '#2a2a2a',
                        borderRadius: 2,
                        transition: 'width 0.7s ease',
                      }} />
                    </div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#333', marginTop: 2 }}>
                      {hits}/10 correct
                    </div>
                  </div>

                  {/* Score */}
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 24, fontWeight: 700,
                    color: isLeader ? '#f9b000' : '#666', flexShrink: 0,
                  }}>
                    {row.score}
                    <span style={{ fontSize: 10, color: '#333', marginLeft: 2 }}>pts</span>
                  </span>
                </div>
              );
            }) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2a2a2a', fontFamily: 'var(--font-mono)', fontSize: 13, letterSpacing: '0.1em' }}>
                NO ENTRIES
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── PIT STOP FOOTER ───────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '0 24px 0 20px',
        borderTop: '1px solid #161616',
        background: '#0c0c0c',
        overflow: 'hidden',
      }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700, color: '#e8002d', letterSpacing: '0.18em', textTransform: 'uppercase', flexShrink: 0 }}>
          Pit Stops
        </span>

        <div style={{ display: 'flex', gap: 10, flex: 1, overflow: 'hidden', alignItems: 'center' }}>
          {data?.recentPits?.length > 0
            ? data.recentPits.map((pit, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: '#111', borderRadius: 4, padding: '5px 12px',
                  borderLeft: `3px solid ${pit.teamColour ? '#' + pit.teamColour : '#333'}`,
                  flexShrink: 0,
                }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14, color: '#e0e0e0' }}>{pit.acronym}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#f9b000', fontWeight: 700 }}>{Number(pit.duration).toFixed(1)}s</span>
                  {pit.lap && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#444' }}>L{pit.lap}</span>}
                </div>
              ))
            : <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#222' }}>No pit stops yet</span>
          }
        </div>

        {/* Refresh countdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f9b000', display: 'inline-block', animation: 'pwpulse 1.4s ease-in-out infinite' }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#333' }}>
            {countdown}s
          </span>
        </div>
      </div>
    </div>
    </React.Fragment>
  );
}
